/**
 * navigation/rootNavigator.tsx
 * Top-level gate. Decides — once, on boot, and on every auth transition —
 * whether to show the auth stack or the app shell:
 *
 *   • An email deep link (?mode=verify/reset/forgot) opens the matching auth
 *     screen straight away (takes priority over the session probe).
 *   • Otherwise we probe the platform session (GET /v1/me via httpOnly cookie):
 *       200 + email_verified        → app shell
 *       200 + email NOT verified    → auth stack at resend-verification (org is
 *                                     inactive until verified; a reload can't
 *                                     slip past this gate)
 *       401                         → auth stack at login
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, Platform, ActivityIndicator } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';
import { getCurrentUser, logoutUser } from '../services/auth/auth';

import AuthStack from './authStack';
import AppShell from './appShell';
import { AuthFlowProvider, type EnterAppOptions } from './authFlowContext';
import { PlatformProvider } from '../context/platformContext';
import { readAuthDeepLink, readBillingRequestLink, readSignedOut, readSignupIntent } from './linking';
import { captureHandoffFromUrl, captureProductFromVerifyLink, redirectToProduct } from './productHandoff';
import { capturePrepaidFromUrl } from './prepaidOrder';
import type { AuthStackParamList, ShellRoute } from './types';

type Phase = 'booting' | 'auth' | 'app';

interface AuthEntry {
  route: keyof AuthStackParamList;
  params?: Record<string, unknown>;
}

export default function RootNavigator() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { refresh: refreshSession } = useSession();

  const [phase, setPhase] = useState<Phase>('booting');
  const [authEntry, setAuthEntry] = useState<AuthEntry>({ route: 'login' });
  // Where the shell opens on entry. Set by the post-onboarding "Go Live" flow so
  // a freshly-onboarded user lands on Settings; undefined → default (home).
  const [initialShellRoute, setInitialShellRoute] = useState<ShellRoute | undefined>(
    () => (readBillingRequestLink() ? { area: 'tenant', screen: 'plan' } : undefined),
  );

  // Boot decision — runs once.
  useEffect(() => {
    // Centralized auth: if the user arrived from a product CTA, state-verify and
    // persist the handoff now so it survives the rest of the flow (incl. the
    // signup→verify→login round-trip). No-op when there are no handoff params.
    captureHandoffFromUrl();
    // Back from Cashfree after paying on the website: keep the order and its
    // claim secret for the signup form, and take them out of the URL.
    capturePrepaidFromUrl();
    // A verification link names its product: open it after the sign-in that follows.
    captureProductFromVerifyLink();

    const deepLink = readAuthDeepLink();
    if (deepLink) {
      setAuthEntry({ route: deepLink.route, params: deepLink.params });
      setPhase('auth');
      return;
    }
    // A product "Start free" CTA arrives with ?mode=signup. This does NOT bypass
    // the probe below — an existing session still wins → dashboard/product. It
    // only picks the unauthenticated entry screen: signup instead of login.
    const unauthedRoute: keyof AuthStackParamList = readSignupIntent() ? 'signup' : 'login';
    // Just signed out in a product: no session to probe — no /me, no /refresh.
    if (readSignedOut()) {
      setAuthEntry({ route: unauthedRoute });
      setPhase('auth');
      return;
    }
    let active = true;
    getCurrentUser()
      .then(async (me: any) => {
        if (!active) return;
        const user = me?.data?.user ?? me?.user;
        if (user && user.email_verified === false) {
          setAuthEntry({ route: 'resendVerification', params: { email: user.email ?? '' } });
          setPhase('auth');
          return;
        }
        // Verified but the profile is still blank → send the user to the Login
        // screen, not straight into personalInfo. They sign in explicitly
        // after verifying their email; login then routes on to the "Tell us
        // about you" card for incomplete profiles.
        //
        // Reached only by LEGACY accounts — the single-flow signup returns
        // profile_complete:true, so anyone created through today's form falls
        // through to the shell.
        if (user && user.profile_complete === false) {
          setAuthEntry({ route: 'login' });
          setPhase('auth');
          return;
        }
        // Already signed in. If a product handoff is pending, send the user
        // straight back into that product (the skip-auth shortcut) rather than
        // into the platform shell.
        const leaving = await redirectToProduct(getCurrentUser);
        if (!active || leaving) return; // navigating away — keep the boot spinner
        setPhase('app');
      })
      .catch(() => { if (active) { setAuthEntry({ route: unauthedRoute }); setPhase('auth'); } });
    return () => { active = false; };
  }, []);

  // Verified sign-in → enter the app (refresh /me; the app stack opens on its
  // own initial route, dashboard).
  const enterApp = useMemo(
    () => ({
      enterApp: (opts?: EnterAppOptions) => {
        // Onboarding "Go Live" passes the Settings route here; record it before
        // the shell mounts so it opens there instead of the default home.
        if (opts?.initialShellRoute) setInitialShellRoute(opts.initialShellRoute);
        // Came from a product CTA → redirect back into the product (logged in)
        // instead of entering the platform shell. Falls through to the shell
        // when there's no handoff or the target is rejected.
        redirectToProduct(getCurrentUser)
          .then((leaving) => {
            if (leaving) return;
            refreshSession();
            setPhase('app');
          })
          .catch(() => {
            refreshSession();
            setPhase('app');
          });
      },
    }),
    [refreshSession],
  );

  // Sign out → clear the platform session, return to login.
  const handleSignOut = async () => {
    try { await logoutUser(); } finally {
      setAuthEntry({ route: 'login' });
      setPhase('auth');
    }
  };

  if (phase === 'booting') {
    return (
      <View style={[styles.boot, { backgroundColor: colors.paper }]}>
        <ActivityIndicator size="large" color={colors.accent} />
      </View>
    );
  }

  if (phase === 'app') {
    return (
      <PlatformProvider>
        <AppShell onSignOut={handleSignOut} initialRoute={initialShellRoute} />
      </PlatformProvider>
    );
  }

  return (
    <AuthFlowProvider value={enterApp}>
      <AuthStack initialRouteName={authEntry.route} initialParams={authEntry.params} />
    </AuthFlowProvider>
  );
}

const styles = StyleSheet.create({
  boot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { height: '100vh' as any } : {}),
  },
});
